import { useCallback, useSyncExternalStore } from 'react';
import { gridTranslations } from './gridLocale';

export type Language = 'de' | 'tr' | 'en';
export type TranslationCatalog = Record<string, { tr: string; en: string; de?: string }>;
export type TranslationValues = Record<string, string | number>;
export const languageNames: Record<Language, string> = { de: 'Deutsch', tr: 'Türkçe', en: 'English' };
const localeNames: Record<Language, string> = { de: 'de-DE', tr: 'tr-TR', en: 'en-GB' };
const gridLocaleTexts = Object.fromEntries((['de', 'tr', 'en'] as const).map((code, index) =>
  [code, Object.fromEntries(Object.entries(gridTranslations).map(([key, entry]) => [key, entry[index]]))]
)) as Record<Language, Record<string, string>>;
const preferenceKey = 'kombinas.language';
const listeners = new Set<() => void>();
function validLanguage(value: unknown): value is Language { return value === 'de' || value === 'tr' || value === 'en'; }
function initialLanguage(): Language {
  try { const saved = localStorage.getItem(preferenceKey); return validLanguage(saved) ? saved : 'de'; } catch { return 'de'; }
}
let language: Language = initialLanguage();

const common: TranslationCatalog = {
  'Sprache': { tr: 'Dil', en: 'Language' },
  'Wird geladen …': { tr: 'Yükleniyor …', en: 'Loading …' },
  'Daten werden geladen …': { tr: 'Veriler yükleniyor …', en: 'Loading data …' },
  'Keine Einträge.': { tr: 'Kayıt bulunamadı.', en: 'No records.' },
  'Abbrechen': { tr: 'İptal', en: 'Cancel' },
  'Verwerfen': { tr: 'Vazgeç', en: 'Discard' },
  'Bestätigen': { tr: 'Onayla', en: 'Confirm' },
  'Bestätigung': { tr: 'Onay', en: 'Confirmation' },
  'Schließen': { tr: 'Kapat', en: 'Close' },
  '{title} schließen': { tr: '{title} kapat', en: 'Close {title}' },
  'Speichern': { tr: 'Kaydet', en: 'Save' },
  'Aktualisieren': { tr: 'Yenile', en: 'Refresh' },
  'Neu': { tr: 'Yeni', en: 'New' },
  'Bearbeiten': { tr: 'Düzenle', en: 'Edit' },
  'Löschen': { tr: 'Sil', en: 'Delete' },
  'Zurück': { tr: 'Geri', en: 'Previous' },
  'Weiter': { tr: 'İleri', en: 'Next' },
  'Auswählen': { tr: 'Seç', en: 'Select' },
  'Suchen …': { tr: 'Ara …', en: 'Search …' },
  'Filtern …': { tr: 'Filtrele …', en: 'Filter …' },
  'Gleich': { tr: 'Eşittir', en: 'Equals' },
  'Ungleich': { tr: 'Eşit değildir', en: 'Does not equal' },
  'Enthält': { tr: 'İçerir', en: 'Contains' },
  'Enthält nicht': { tr: 'İçermez', en: 'Does not contain' },
  'Beginnt mit': { tr: 'İle başlar', en: 'Begins with' },
  'Endet mit': { tr: 'İle biter', en: 'Ends with' },
  'Leer': { tr: 'Boş', en: 'Blank' },
  'Nicht leer': { tr: 'Boş değil', en: 'Not blank' },
  'UND': { tr: 'VE', en: 'AND' },
  'ODER': { tr: 'VEYA', en: 'OR' },
  'Anwenden': { tr: 'Uygula', en: 'Apply' },
  'Zurücksetzen': { tr: 'Sıfırla', en: 'Reset' },
  'Kleiner als': { tr: 'Küçüktür', en: 'Less than' },
  'Größer als': { tr: 'Büyüktür', en: 'Greater than' },
  'Zwischen': { tr: 'Arasında', en: 'Between' },
  'Anfrage fehlgeschlagen ({status}).': { tr: 'İstek başarısız ({status}).', en: 'Request failed ({status}).' },
  'Verbindung fehlgeschlagen. Bitte erneut versuchen.': { tr: 'Bağlantı kurulamadı. Lütfen tekrar deneyin.', en: 'Connection failed. Please try again.' },
};
const catalogs: TranslationCatalog[] = [];
const entries = new Map<string, TranslationCatalog[string]>();
const reverse = new Map<string, string>();
const templates: { source: string; expression: RegExp; names: string[] }[] = [];
const conflicts = new Set<string>();
function addCatalog(catalog: TranslationCatalog) {
  catalogs.push(catalog);
  for (const [source, entry] of Object.entries(catalog)) {
    const existing = entries.get(source);
    if (existing && (existing.tr !== entry.tr || existing.en !== entry.en || (existing.de ?? source) !== (entry.de ?? source))) {
      conflicts.add(source);
      continue;
    }
    if (existing) continue;
    entries.set(source, entry);
    for (const text of [source, entry.de ?? source, entry.tr, entry.en]) {
      if (!reverse.has(text)) reverse.set(text, source);
      if (!/\{\w+\}/.test(text)) continue;
      const names: string[] = [];
      let pattern = ''; let previous = 0;
      for (const match of text.matchAll(/\{(\w+)\}/g)) {
        pattern += text.slice(previous, match.index).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(.+?)';
        names.push(match[1]); previous = match.index! + match[0].length;
      }
      pattern += text.slice(previous).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      templates.push({ source, expression: new RegExp(`^${pattern}$`, 's'), names });
    }
  }
}
addCatalog(common);
const moduleCatalogs = import.meta.glob<{ translations: TranslationCatalog }>(['../modules/*/locale.ts', '../shell/locale.ts'], { eager: true });
for (const catalog of Object.values(moduleCatalogs)) if (catalog.translations) addCatalog(catalog.translations);

export function registerTranslations(catalog: TranslationCatalog): void { addCatalog(catalog); }
export function getTranslationConflicts(): string[] { return [...conflicts].sort(); }
export function getTranslationSources(): string[] { return [...entries.keys()]; }
export function translateFor(selected: Language, source: string, values?: TranslationValues): string {
  let canonical = entries.has(source) ? source : reverse.get(source) ?? source;
  let parameters = values;
  if (!entries.has(canonical)) {
    for (const template of templates) {
      const match = template.expression.exec(source);
      if (match) {
        canonical = template.source;
        parameters = { ...Object.fromEntries(template.names.map((name, index) => [name, match[index + 1]])), ...values };
        break;
      }
    }
  }
  const entry = entries.get(canonical);
  const message = entry ? selected === 'de' ? entry.de ?? canonical : entry[selected] : source;
  return parameters ? message.replace(/\{([\w]+)\}/g, (placeholder, name: string) => String(parameters![name] ?? placeholder)) : message;
}
export class LocalizedError extends Error {
  constructor(public messageSource: string, public messageParameters?: TranslationValues) {
    super(translateFor('de', messageSource, messageParameters)); this.name = 'LocalizedError';
  }
}
export function translateError(error: unknown, selected: Language = language): string {
  if (error && typeof error === 'object') {
    const detail = error as { messageSource?: string; messageParameters?: TranslationValues; message?: string; messageTranslations?: Partial<Record<Language, string>> };
    if (detail.messageTranslations?.[selected]) return detail.messageTranslations[selected]!;
    if (detail.messageSource) return translateFor(selected, detail.messageSource, detail.messageParameters);
    if (error instanceof TypeError && /fetch|network|load failed/i.test(error.message)) return translateFor(selected, 'Verbindung fehlgeschlagen. Bitte erneut versuchen.');
    if (detail.message) return translateFor(selected, detail.message);
  }
  return translateFor(selected, String(error ?? ''));
}
export function getLanguage(): Language { return language; }
export function getLocale(): string { return localeNames[language]; }
export function translate(source: string, values?: TranslationValues): string { return translateFor(language, source, values); }
/** Stable AG Grid callback: AG Grid marks localeText/getLocaleText as initial-only. */
export function getGridLocaleText(params: { key: string; defaultValue: string; variableValues?: string[] }): string {
  const entry = gridTranslations[params.key];
  const text = entry ? entry[language === 'de' ? 0 : language === 'tr' ? 1 : 2] : translate(params.defaultValue);
  return text.replace(/\{(\d+)\}/g, (placeholder, index: string) => params.variableValues?.[Number(index)] ?? placeholder);
}
export function setLanguage(next: Language): void {
  if (!validLanguage(next)) return;
  const changed = language !== next; language = next;
  try { localStorage.setItem(preferenceKey, next); } catch { /* Preference storage is optional. */ }
  if (typeof document !== 'undefined') document.documentElement.lang = next;
  if (changed) listeners.forEach(listener => listener());
}
if (typeof document !== 'undefined') document.documentElement.lang = language;
if (typeof window !== 'undefined') window.addEventListener('storage', event => {
  if (event.key === preferenceKey && validLanguage(event.newValue)) setLanguage(event.newValue);
});
function subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; }

export function useI18n() {
  const selected = useSyncExternalStore(subscribe, getLanguage, () => 'de' as Language);
  const t = useCallback((source: string, values?: TranslationValues) => translateFor(selected, source, values), [selected]);
  const locale = localeNames[selected];
  const formatNumber = useCallback((value: number, options?: Intl.NumberFormatOptions) => new Intl.NumberFormat(locale, options).format(value), [locale]);
  const formatDate = useCallback((value: Date | string | number, options?: Intl.DateTimeFormatOptions) => {
    const date = value instanceof Date ? value : new Date(typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
    return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat(locale, options).format(date);
  }, [locale]);
  return { language: selected, setLanguage, t, locale, formatNumber, formatDate, getGridLocaleText, gridLocaleText: gridLocaleTexts[selected] };
}
